import { IconPlus } from "@tabler/icons-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowDropDownIcon from "@/assets/icons/arrowDropDown.svg?react";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import { RangeCalendar } from "@/components/atomic/calendar/RangeCalendar";
import Checkbox from "@/components/atomic/Checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/atomic/DropdownMenu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/atomic/Popover";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { CategoryGroupItem } from "@/components/ui/CategoryGroupItem";
import { cn } from "@/lib/utils";

interface CategoryGroup {
  parentKey: string;
  parentParts: string[];
  childPaths: string[];
}

interface BasicFilterTabProps {
  onOpenCategoryModal: () => void;
  filterContainerRef: React.RefObject<HTMLDivElement>;
  categories: string[];
  vocPeriodStart: Date | null;
  vocPeriodEnd: Date | null;
  vocPeriodType: "all" | "custom";
  onUpdateState: (updates: {
    categories?: string[];
    vocPeriodStart?: Date | null;
    vocPeriodEnd?: Date | null;
    vocPeriodType?: "all" | "custom";
  }) => void;
}

export const BasicFilterTab: React.FC<BasicFilterTabProps> = ({
  onOpenCategoryModal,
  filterContainerRef,
  categories,
  vocPeriodStart,
  vocPeriodEnd,
  vocPeriodType,
  onUpdateState,
}) => {
  const { t } = useTranslation("voiceek_ui");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleToggleAllCategories = () => {
    onUpdateState({ categories: [] });
  };

  const onClickWhiteSpace = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-no-bubble]")) {
      e.stopPropagation();
      return;
    }
    onOpenCategoryModal();
  };

  const groupCategoriesByParent = (categories: string[]): CategoryGroup[] => {
    const grouped = categories.reduce((acc, categoryValue) => {
      const parts = categoryValue.split("/").filter(Boolean);
      const parentKey = parts.length > 1
        ? "/" + parts.slice(0, -1).join("/") + "/"
        : categoryValue;

      if (!acc[parentKey]) {
        acc[parentKey] = [];
      }
      acc[parentKey].push(categoryValue);
      return acc;
    }, {} as Record<string, string[]>);

    return Object.entries(grouped).map(([parentKey, childPaths]) => ({
      parentKey,
      parentParts: parentKey.split("/").filter(Boolean),
      childPaths,
    }));
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 対象期間（分析）セクション */}
      <div className="flex gap-6 items-center">
        <div className="w-[60px] text-[14px] font-semibold text-[#333333]">
          {t("chat.filter.targetPeriod")}
        </div>
        <div className="w-[2px] h-9 bg-[#d9d9d9]" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-9 w-[120px] border border-[#b7b7b7] rounded-[5px] px-3 bg-white text-[14px] text-[#333333] flex items-center justify-between hover:border-[#6888E1] hover:cursor-pointer focus:outline-none focus:border-[#6888E1]">
                  {vocPeriodType === "all"
                    ? t("chat.filter.analysisPeriod.all")
                    : t("chat.filter.analysisPeriod.custom")}

                  <span className="flex items-center justify-end w-[14px] h-5">
                    <ArrowDropDownIcon className="text-[#6888E1]" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[120px] bg-white  p-0">
                <DropdownMenuItem
                  onClick={() => {
                    onUpdateState({ vocPeriodType: "all" });
                  }}
                  className="text-[14px] text-[#333333] px-3 py-2 focus:bg-[#6888E1] focus:text-white cursor-pointer"
                >
                  {t("chat.filter.analysisPeriod.all")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    onUpdateState({ vocPeriodType: "custom" });
                  }}
                  className="text-[14px] text-[#333333] px-3 py-2 focus:bg-[#6888E1] focus:text-white cursor-pointer"
                >
                  {t("chat.filter.analysisPeriod.custom")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {vocPeriodType !== "all" && (
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "h-9 px-3 border rounded-[5px] flex items-center gap-2 focus:outline-none bg-white border-[#b7b7b7] hover:border-[#6888E1] hover:cursor-pointer focus:border-[#6888E1]",
                    )}
                  >
                    <div className="flex items-center gap-1">
                      <span className="flex items-center justify-center w-6 h-6">
                        <CalendarIcon className="w-fit h-fit text-[#5F6368]" />
                      </span>
                      <span
                        className={cn(
                          "text-[14px] text-[#333333]",
                        )}
                      >
                        {vocPeriodStart || vocPeriodEnd
                          ? (
                            <>
                              {vocPeriodStart && (
                                <span>
                                  {vocPeriodStart.toLocaleDateString("ja-JP", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })}
                                </span>
                              )}
                              {" - "}
                              {vocPeriodEnd && (
                                <span>
                                  {vocPeriodEnd.toLocaleDateString("ja-JP", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })}
                                </span>
                              )}
                            </>
                          )
                          : <span className="text-[#999999]">{t("chat.filter.selectDate")}</span>}
                      </span>
                    </div>
                    <span className="flex items-center justify-end w-[14px] h-5">
                      <ArrowDropDownIcon className="text-[#6888E1]" />
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  container={filterContainerRef.current}
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="z-30"
                >
                  <RangeCalendar
                    initialDateRange={{
                      from: vocPeriodStart,
                      to: vocPeriodEnd,
                    }}
                    onConfirm={(range) => {
                      setIsCalendarOpen(false);
                      onUpdateState({ vocPeriodStart: range.from, vocPeriodEnd: range.to });
                    }}
                    onCancel={() => setIsCalendarOpen(false)}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </div>

      {/* カテゴリセクション */}
      <div className="flex gap-6 items-center">
        <div className="w-[60px] text-[14px] font-semibold text-[#333333]">
          {t("chat.filter.category")}
        </div>
        <div className="w-[2px]  bg-[#d9d9d9] self-stretch" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="filter-all-categories"
              checked={categories.length === 0}
              disabled={categories.length === 0}
              onCheckedChange={handleToggleAllCategories}
              className={cn("hover:cursor-pointer")}
            />
            <label htmlFor="filter-all-categories" className="text-[14px] text-[#474747] cursor-pointer">
              {t("chat.filter.all")}
            </label>
          </div>

          <button
            className="bg-white border border-[#b7b7b7] rounded-[5px] p-3 hover:cursor-pointer"
            onClick={(e) => onClickWhiteSpace(e)}
          >
            <div className="flex items-center gap-1.5 text-left text-[#6888E1]">
              <IconPlus className="w-[14px] h-[14px]" />
              <span className="text-[14px]">
                {t("chat.filter.selectCategory")}
              </span>
            </div>

            {categories.length > 0 && (
              <ScrollArea
                className="mt-2"
                viewportClassName="max-h-22"
              >
                <div className="flex flex-col gap-2 pr-3">
                  {groupCategoriesByParent(categories).map(({ parentKey, parentParts, childPaths }) => (
                    <CategoryGroupItem
                      key={parentKey}
                      parentParts={parentParts}
                      childPaths={childPaths}
                      onRemove={() => {
                        const remainingCategories = categories.filter(c => !childPaths.includes(c));
                        onUpdateState({ categories: remainingCategories });
                      }}
                    />
                  ))}
                </div>
                <ScrollBar orientation="vertical" />
              </ScrollArea>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
